import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, FormControl, FormControlLabel, IconButton,
  InputLabel, Modal, OutlinedInput, Select, MenuItem, Switch,
} from '@material-ui/core';
import { Close, ChevronLeft, ChevronRight, DeleteForeverOutlined } from '@material-ui/icons';
import { toast } from 'react-toastify';
import { apiLabs } from '../../../Services/api';
import useHolidayStyle from './holidayStyle';

const TYPE_OPTIONS = [
  { value: 'nacional',    label: 'Nacional' },
  { value: 'estadual',    label: 'Estadual' },
  { value: 'municipal',   label: 'Municipal' },
  { value: 'facultativo', label: 'Facultativo' },
  { value: 'interno',     label: 'Interno (folga)' },
];

const TYPE_COLORS = {
  nacional:    { bg: '#dbeafe', color: '#1e40af' },
  estadual:    { bg: '#e0e7ff', color: '#3730a3' },
  municipal:   { bg: '#f3e8ff', color: '#6b21a8' },
  facultativo: { bg: '#fef3c7', color: '#92400e' },
  interno:     { bg: '#fee2e2', color: '#b91c1c' },
};

// Formata Date → YYYY-MM-DD (no fuso local, sem UTC shift)
function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Parse YYYY-MM-DD como Date no fuso local (não UTC)
function parseISODate(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const DAY_HEADERS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const EMPTY_FORM = {
  id: null,
  date: '',
  name: '',
  type: 'interno',
  uf: '',
  city: '',
  blocks_shipping: true,
};

export default function HolidayModal({ open, onClose }) {
  const styles = useHolidayStyle();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formOpen, setFormOpen] = useState(false);

  // ── Fetch feriados do ano visível ─────────────────────────────────────
  const year = month.getFullYear();

  async function fetchHolidays() {
    setLoading(true);
    try {
      const res = await apiLabs.get(`feriados?ano=${year}&blocks=false`);
      setHolidays(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('GET feriados failed:', err);
      toast.error('Falha ao carregar feriados');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) fetchHolidays();
  }, [open, year]); // eslint-disable-line

  // ── Map de feriados por data pra lookup rápido ────────────────────────
  const holidaysByDate = useMemo(() => {
    const map = {};
    holidays.forEach((h) => {
      const key = String(h.date).slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(h);
    });
    return map;
  }, [holidays]);

  // ── Grid do calendário (6 semanas x 7 dias) ───────────────────────────
  const calendarCells = useMemo(() => {
    const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
    const startWeekday = firstDay.getDay(); // 0 = Domingo
    const cells = [];
    // dias do mês anterior pra preencher a 1ª semana
    for (let i = startWeekday; i > 0; i--) {
      const d = new Date(firstDay);
      d.setDate(d.getDate() - i);
      cells.push({ date: d, outside: true });
    }
    // dias do mês atual
    const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      cells.push({ date: new Date(month.getFullYear(), month.getMonth(), d), outside: false });
    }
    // dias do próximo mês pra completar até 42 células (6 semanas)
    while (cells.length < 42) {
      const last = cells[cells.length - 1].date;
      const d = new Date(last);
      d.setDate(d.getDate() + 1);
      cells.push({ date: d, outside: true });
    }
    return cells;
  }, [month]);

  function prevMonth() {
    setMonth((m) => {
      const d = new Date(m);
      d.setMonth(d.getMonth() - 1);
      return d;
    });
  }
  function nextMonth() {
    setMonth((m) => {
      const d = new Date(m);
      d.setMonth(d.getMonth() + 1);
      return d;
    });
  }

  function onDayClick(cellDate) {
    const iso = toISODate(cellDate);
    const list = holidaysByDate[iso] || [];
    if (list.length > 0) {
      // Edita o primeiro feriado do dia
      const h = list[0];
      setForm({
        id: h.id,
        date: iso,
        name: h.name || '',
        type: h.type || 'interno',
        uf: h.uf || '',
        city: h.city || '',
        blocks_shipping: h.blocks_shipping !== false,
      });
    } else {
      // Novo feriado
      setForm({ ...EMPTY_FORM, date: iso });
    }
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setForm({ ...EMPTY_FORM });
  }

  function handleField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function saveForm(e) {
    e.preventDefault();
    if (!form.date) { toast.warn('Escolha uma data'); return; }
    if (!form.name.trim()) { toast.warn('Informe o nome do feriado'); return; }

    const payload = {
      date: form.date,
      name: form.name.trim(),
      type: form.type || null,
      uf: form.type === 'estadual' ? (form.uf || '').toUpperCase().slice(0, 2) : null,
      city: form.type === 'municipal' ? (form.city || '').trim() : null,
      blocks_shipping: !!form.blocks_shipping,
    };

    setSaving(true);
    try {
      if (form.id) {
        await apiLabs.put(`feriados/${form.id}`, payload);
        toast.success('Feriado atualizado');
      } else {
        await apiLabs.post('feriados', payload);
        toast.success('Feriado cadastrado');
      }
      closeForm();
      fetchHolidays();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error || err.message;
      if (status === 409) {
        toast.warn(msg || 'Já existe feriado nessa data');
      } else {
        toast.error('Erro ao salvar feriado: ' + msg);
      }
    } finally {
      setSaving(false);
    }
  }

  async function deleteHoliday() {
    if (!form.id) return;
    if (!window.confirm(`Remover o feriado "${form.name}" de ${form.date}?`)) return;
    setSaving(true);
    try {
      await apiLabs.delete(`feriados/${form.id}`);
      toast.success('Feriado removido');
      closeForm();
      fetchHolidays();
    } catch (err) {
      toast.error('Erro ao remover feriado');
    } finally {
      setSaving(false);
    }
  }

  const today = toISODate(new Date());

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modalBox}>
        <div className={styles.header}>
          <div>
            <h2>Feriados & Folgas</h2>
            <span className="subtitle">
              Clique num dia pra cadastrar feriado ou folga. Datas com bloqueio contam como não-úteis no cálculo de embarque.
            </span>
          </div>
          <IconButton className="closeBtn" size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </div>

        <div className={styles.body}>
          <div className={styles.calendarSide}>
            {/* Toolbar do calendário */}
            <div className={styles.calendarToolbar}>
              <IconButton size="small" onClick={prevMonth}><ChevronLeft /></IconButton>
              <div className={styles.monthLabel}>
                {MONTH_NAMES[month.getMonth()]} {year}
              </div>
              <IconButton size="small" onClick={nextMonth}><ChevronRight /></IconButton>
              {loading && <span className={styles.loadingHint}>carregando…</span>}
            </div>

            {/* Cabeçalho dos dias da semana */}
            <div className={styles.weekHeader}>
              {DAY_HEADERS.map((d) => <span key={d}>{d}</span>)}
            </div>

            {/* Grid do calendário */}
            <div className={styles.calendarGrid}>
              {calendarCells.map((cell, i) => {
                const iso = toISODate(cell.date);
                const list = holidaysByDate[iso] || [];
                const isToday = iso === today;
                const selected = form.date === iso && formOpen;
                return (
                  <button
                    key={i}
                    type="button"
                    className={[
                      styles.dayCell,
                      cell.outside && 'outside',
                      isToday && 'today',
                      selected && 'selected',
                      list.length > 0 && 'hasHoliday',
                    ].filter(Boolean).join(' ')}
                    onClick={() => onDayClick(cell.date)}
                  >
                    <span className="dayNum">{cell.date.getDate()}</span>
                    {list.length > 0 && (
                      <div className="badges">
                        {list.slice(0, 2).map((h) => (
                          <span
                            key={h.id}
                            className="badge"
                            style={{
                              background: TYPE_COLORS[h.type]?.bg || '#e5e7eb',
                              color: TYPE_COLORS[h.type]?.color || '#374151',
                            }}
                            title={h.name}
                          >
                            {h.name}
                          </span>
                        ))}
                        {list.length > 2 && (
                          <span className="badge more">+{list.length - 2}</span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Legenda */}
            <div className={styles.legend}>
              {TYPE_OPTIONS.map((t) => (
                <span key={t.value} className="legendItem">
                  <span
                    className="swatch"
                    style={{
                      background: TYPE_COLORS[t.value].bg,
                      borderColor: TYPE_COLORS[t.value].color,
                    }}
                  />
                  {t.label}
                </span>
              ))}
            </div>
          </div>

          {/* Painel lateral: form */}
          {formOpen && (
            <form onSubmit={saveForm} className={styles.formSide}>
              <div className={styles.formHeader}>
                <div>
                  <span className={styles.formLabel}>Data selecionada</span>
                  <div className={styles.formDate}>
                    {form.date && parseISODate(form.date)?.toLocaleDateString('pt-BR', {
                      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
                    })}
                  </div>
                </div>
                {form.id && (
                  <IconButton
                    size="small"
                    onClick={deleteHoliday}
                    disabled={saving}
                    className={styles.deleteBtn}
                    title="Remover feriado"
                  >
                    <DeleteForeverOutlined />
                  </IconButton>
                )}
              </div>

              <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: 12 }}>
                <InputLabel>Nome do feriado *</InputLabel>
                <OutlinedInput
                  value={form.name}
                  onChange={(e) => handleField('name', e.target.value)}
                  label="Nome do feriado *"
                  autoFocus
                />
              </FormControl>

              <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: 10 }}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={form.type}
                  onChange={(e) => handleField('type', e.target.value)}
                  label="Tipo"
                >
                  {TYPE_OPTIONS.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {form.type === 'estadual' && (
                <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: 10 }}>
                  <InputLabel>UF</InputLabel>
                  <OutlinedInput
                    value={form.uf}
                    onChange={(e) => handleField('uf', e.target.value.toUpperCase().slice(0, 2))}
                    label="UF"
                    placeholder="SP"
                  />
                </FormControl>
              )}

              {form.type === 'municipal' && (
                <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: 10 }}>
                  <InputLabel>Cidade</InputLabel>
                  <OutlinedInput
                    value={form.city}
                    onChange={(e) => handleField('city', e.target.value)}
                    label="Cidade"
                    placeholder="São Paulo"
                  />
                </FormControl>
              )}

              <div style={{ marginTop: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!form.blocks_shipping}
                      onChange={(e) => handleField('blocks_shipping', e.target.checked)}
                    />
                  }
                  label={form.blocks_shipping ? 'Bloqueia coleta neste dia' : 'Não bloqueia coleta'}
                />
              </div>

              <div className={styles.formActions}>
                <Button className={styles.cancelBtn} onClick={closeForm} disabled={saving}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  className={styles.submitBtn}
                  disabled={saving || !form.name.trim()}
                  disableElevation
                >
                  {saving ? 'Salvando…' : (form.id ? 'Salvar alterações' : 'Cadastrar feriado')}
                </Button>
              </div>
            </form>
          )}

          {!formOpen && (
            <div className={styles.hintSide}>
              <div className={styles.hintIcon}>📅</div>
              <div className={styles.hintTitle}>Selecione um dia</div>
              <div className={styles.hintText}>
                Clique em qualquer dia do calendário para cadastrar um feriado ou folga.
                Dias com feriado já registrado abrem em modo de edição.
              </div>
            </div>
          )}
        </div>
      </Box>
    </Modal>
  );
}

